import { Controller } from '@hotwired/stimulus'
import { renderPdf, watchContainerWidth } from '../lib/pdf_renderer'
import {
  projectPlacement as computeProjection,
  applyProjection,
} from '../lib/placement_projection'

const HANDLE_DIRECTIONS = ['nw', 'ne', 'se', 'sw', 'n', 's', 'e', 'w']

const HANDLE_POSITION_CLASSES = {
  nw: '-top-1.5 -left-1.5 cursor-nwse-resize',
  ne: '-top-1.5 -right-1.5 cursor-nesw-resize',
  se: '-bottom-1.5 -right-1.5 cursor-nwse-resize',
  sw: '-bottom-1.5 -left-1.5 cursor-nesw-resize',
  n: '-top-1.5 left-1/2 -translate-x-1/2 cursor-ns-resize',
  s: '-bottom-1.5 left-1/2 -translate-x-1/2 cursor-ns-resize',
  e: 'top-1/2 -right-1.5 -translate-y-1/2 cursor-ew-resize',
  w: 'top-1/2 -left-1.5 -translate-y-1/2 cursor-ew-resize',
}

// Inline SVG (currentColor) for icons drawn inside placed zones from JS.
const INLINE_ICONS = {
  grip:
    '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
    'stroke-linejoin="round"><circle cx="9" cy="12" r="1"/>' +
    '<circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>' +
    '<circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/>' +
    '<circle cx="15" cy="19" r="1"/></svg>',
  pen:
    '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
    'stroke-linejoin="round"><path d="M12 20h9"/>' +
    '<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>',
  x:
    '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" ' +
    'stroke="currentColor" stroke-width="2.5" stroke-linecap="round" ' +
    'stroke-linejoin="round"><path d="M18 6 6 18"/>' +
    '<path d="m6 6 12 12"/></svg>',
}

function interpolate(template, vars) {
  return Object.entries(vars).reduce(
    (output, [key, value]) =>
      output.replaceAll(`%{${key}}`, value === undefined ? '' : String(value)),
    template
  )
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

export default class extends Controller {
  static targets = [
    'stage',
    'loading',
    'chipsList',
    'chip',
    'counter',
    'counterText',
    'instruction',
    'instructionText',
  ]

  static values = {
    documentId: Number,
    url: String,
    placementWidth: Number,
    placementHeight: Number,
    minWidth: Number,
    maxWidth: Number,
    minHeight: Number,
    maxHeight: Number,
    initialPlacements: Array,
    signers: Array,
    mode: { type: String, default: 'parallel' },
    remove: String,
    placedLabel: String,
    counterOne: String,
    counterOther: String,
    locale: { type: String, default: 'fr' },
    instruction: String,
    instructionRemaining: String,
    zoneLabel: { type: String, default: 'Signature' },
    dropHint: { type: String, default: '%{name}' },
    sequentialLabel: { type: String, default: '%{name} (%{index})' },
    unnamedLabel: { type: String, default: 'Signer %{index}' },
  }

  initialize() {
    this.placements = []
    this.placementCounter = 0
    this.pagesMap = new Map()
    this.dragging = false
    this.rendering = false
  }

  async connect() {
    this.bindChipPointerDrag()

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }

    try {
      const renderedPages = await renderPdf(this.urlValue, this.stageTarget)
      this.wireRenderedPages(renderedPages)
      this.hydrateInitialPlacements()
      this.notifyPlacementsChange()
    } catch (error) {
      console.error('PDF render failed', error)
    } finally {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add('hidden')
      }
    }

    this.stopWatching = watchContainerWidth(
      this.stageTarget,
      () => this.rerender()
    )
  }

  disconnect() {
    if (this.stopWatching) this.stopWatching()
  }

  async rerender() {
    if (this.rendering || this.dragging) return
    this.rendering = true
    const snapshot = this.placements.map((placement) => ({
      signerRef: placement.signerRef,
      color: placement.color,
      page: placement.page,
      x: placement.x,
      y: placement.y,
      width: placement.width,
      height: placement.height,
    }))
    try {
      this.placements = []
      this.placementCounter = 0
      this.pagesMap = new Map()
      this.stageTarget.innerHTML = ''
      const renderedPages = await renderPdf(this.urlValue, this.stageTarget)
      this.wireRenderedPages(renderedPages)
      snapshot.forEach((placement) => this.appendPlacement(placement))
    } catch (error) {
      console.error('PDF re-render failed', error)
    } finally {
      this.rendering = false
    }
  }

  // Pointer-events based drag (uniform across Chrome/Firefox/Safari/Edge +
  // touch/stylus), replacing native HTML5 DnD which can't be styled reliably
  // and ignores touch. Delegation on the chips list; per-chip pointer capture.
  bindChipPointerDrag() {
    if (!this.hasChipsListTarget) return

    this.chipsListTarget.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return
      const chip = event.target.closest('[data-signer-ref]')
      if (!chip || chip.dataset.signerPlaced === 'true') return
      this.beginChipDrag(event, chip)
    })
  }

  beginChipDrag(event, chip) {
    event.preventDefault()
    this.dragging = true
    this.chipDrag = {
      chip,
      signerRef: chip.dataset.signerRef,
      color: chip.dataset.signerColor || '#028090',
      textColor: chip.dataset.signerTextColor || chip.dataset.signerColor,
      name: chip.dataset.signerName || '',
      page: null,
    }

    chip.classList.add('opacity-40')
    this.chipFloat = this.buildFloatingChip(chip)
    this.chipPreview = this.buildZonePreview(this.chipDrag)
    document.body.appendChild(this.chipFloat)
    document.body.appendChild(this.chipPreview)
    this.dropOverlay = this.buildDropOverlay(this.chipDrag.name)
    this.stageTarget.appendChild(this.dropOverlay)
    this.positionChipDragVisuals(event)

    // Listen on window (not pointer-capture on the chip): guarantees the
    // pointerup is always delivered even when the chip lives in a sticky /
    // overflow container, so the gesture can never get stuck.
    this.boundChipMove = (moveEvent) => this.continueChipDrag(moveEvent)
    this.boundChipUp = (upEvent) => this.finishChipDrag(upEvent, chip)
    window.addEventListener('pointermove', this.boundChipMove)
    window.addEventListener('pointerup', this.boundChipUp)
    window.addEventListener('pointercancel', this.boundChipUp)
  }

  continueChipDrag(event) {
    this.positionChipDragVisuals(event)
    const page = this.pageAtPoint(event.clientX, event.clientY)
    this.chipDrag.page = page
    this.toggleDropOverlay(Boolean(page))
  }

  finishChipDrag(event, chip) {
    window.removeEventListener('pointermove', this.boundChipMove)
    window.removeEventListener('pointerup', this.boundChipUp)
    window.removeEventListener('pointercancel', this.boundChipUp)
    chip.classList.remove('opacity-40')

    // try/finally so the drag visuals are always cleaned up, even if
    // appending the placement throws — the UI can never stay blocked.
    try {
      const page =
        event.type === 'pointercancel'
          ? null
          : this.pageAtPoint(event.clientX, event.clientY)
      if (page && this.chipDrag) {
        const { x, y } = this.computeDropCoordinates(event, page)
        this.appendPlacement({
          signerRef: this.chipDrag.signerRef,
          color: this.chipDrag.color,
          page: page.page,
          x,
          y,
        })
        this.refreshChipBadges()
        this.refreshCounter()
        this.notifyPlacementsChange()
      }
    } finally {
      this.cleanupChipDragVisuals()
      this.chipDrag = null
      this.dragging = false
    }
  }

  positionChipDragVisuals(event) {
    if (this.chipFloat) {
      this.chipFloat.style.left = `${event.clientX + 14}px`
      this.chipFloat.style.top = `${event.clientY + 14}px`
    }
    if (this.chipPreview) {
      this.chipPreview.style.left = `${event.clientX - 104}px`
      this.chipPreview.style.top = `${event.clientY - 22}px`
    }
  }

  pageAtPoint(clientX, clientY) {
    for (const pageInfo of this.pagesMap.values()) {
      const rect = pageInfo.canvas.getBoundingClientRect()
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        return pageInfo
      }
    }
    return null
  }

  toggleDropOverlay(active) {
    if (this.dropOverlay) this.dropOverlay.classList.toggle('hidden', !active)
    this.pagesMap.forEach((pageInfo) => {
      if (pageInfo.wrapper) {
        pageInfo.wrapper.style.opacity = active ? '0.5' : ''
      }
    })
  }

  cleanupChipDragVisuals() {
    this.chipFloat?.remove()
    this.chipPreview?.remove()
    this.dropOverlay?.remove()
    this.chipFloat = null
    this.chipPreview = null
    this.dropOverlay = null
    this.pagesMap.forEach((pageInfo) => {
      if (pageInfo.wrapper) pageInfo.wrapper.style.opacity = ''
    })
  }

  buildFloatingChip(chip) {
    const float = chip.cloneNode(true)
    float.removeAttribute('data-document-placer-target')
    float.removeAttribute('data-signer-ref')
    float.className = [
      'fixed z-50 pointer-events-none inline-flex items-center gap-2',
      'rounded-field border-[1.5px] border-solid px-3 py-2',
      'text-sm font-semibold shadow-lg',
    ].join(' ')
    float.style.cssText = [
      'background-color: var(--color-base-100)',
      `border-color: ${this.chipDrag.color}`,
      `color: ${this.chipDrag.textColor}`,
      'transform: rotate(-3deg)',
    ].join(';')
    return float
  }

  buildZonePreview({ name, color, textColor }) {
    const preview = document.createElement('div')
    preview.className = [
      'fixed z-50 pointer-events-none w-52 rounded-field',
      'border-[1.5px] border-dashed px-3 py-2 shadow-lg',
    ].join(' ')
    preview.style.cssText = [
      `background-color: ${color}1A`,
      `border-color: ${color}`,
    ].join(';')

    const header = document.createElement('div')
    header.className = 'flex items-center gap-2 text-[11.5px] font-semibold'
    header.style.color = textColor
    const dot = document.createElement('span')
    dot.className = 'w-[7px] h-[7px] rounded-full shrink-0'
    dot.style.backgroundColor = color
    const label = document.createElement('span')
    label.className = 'truncate'
    label.textContent = name
    header.append(dot, label)

    const body = document.createElement('div')
    body.className = [
      'mt-2 flex items-center justify-center gap-1.5 rounded',
      'border border-dashed text-[11px] py-1.5',
    ].join(' ')
    body.style.borderColor = `${color}80`
    body.style.color = textColor
    body.textContent = this.zoneLabelValue

    preview.append(header, body)
    return preview
  }

  buildDropOverlay(name) {
    const overlay = document.createElement('div')
    overlay.className = [
      'hidden absolute inset-[10px] z-20 rounded-field pointer-events-none',
      'border-[1.5px] border-dashed border-primary/50 bg-primary/5',
    ].join(' ')

    const pill = document.createElement('div')
    pill.className = [
      'absolute left-1/2 -translate-x-1/2 top-2 px-3 py-1 rounded-full',
      'bg-primary text-primary-content text-[11px] font-semibold',
      'whitespace-nowrap',
    ].join(' ')
    pill.textContent = interpolate(this.dropHintValue, { name })
    overlay.appendChild(pill)
    return overlay
  }

  wireRenderedPages(renderedPages) {
    renderedPages.forEach((page) => {
      const wrapper = document.createElement('div')
      wrapper.style.position = 'relative'
      wrapper.style.display = 'block'
      page.canvas.parentNode.insertBefore(wrapper, page.canvas)
      wrapper.appendChild(page.canvas)
      page.wrapper = wrapper

      this.pagesMap.set(page.page, page)
    })
  }

  hydrateInitialPlacements() {
    const initial = Array.from(this.initialPlacementsValue || [])
    initial.forEach((placement) => {
      const page = this.pagesMap.get(Number(placement.page_number))
      if (!page) return

      this.appendPlacement({
        signerRef: placement.signer_ref,
        color: placement.color || this.colorForRef(placement.signer_ref),
        page: page.page,
        x: Number(placement.x),
        y: Number(placement.y),
        width: Number(placement.width) || this.placementWidthValue,
        height: Number(placement.height) || this.placementHeightValue,
      })
    })
    this.refreshChipBadges()
    this.refreshCounter()
  }

  computeDropCoordinates(event, page) {
    const rect = page.canvas.getBoundingClientRect()
    const dynamicScale = rect.width / page.pdfWidth
    const cssX = event.clientX - rect.left
    const cssY = event.clientY - rect.top
    const ptX = cssX / dynamicScale
    const ptYFromTop = cssY / dynamicScale

    const halfWidth = this.placementWidthValue / 2
    const halfHeight = this.placementHeightValue / 2
    const maxX = page.pdfWidth - this.placementWidthValue
    const maxY = page.pdfHeight - this.placementHeightValue
    return {
      x: clamp(ptX - halfWidth, 0, maxX),
      y: clamp(page.pdfHeight - ptYFromTop - halfHeight, 0, maxY),
    }
  }

  appendPlacement({ signerRef, color, page, x, y, width, height }) {
    this.placementCounter += 1
    const placement = {
      id: `p-${this.documentIdValue}-${this.placementCounter}`,
      signerRef,
      color,
      page,
      x,
      y,
      width: width || this.placementWidthValue,
      height: height || this.placementHeightValue,
    }
    this.placements.push(placement)
    this.renderPlacement(placement)
    return placement
  }

  renderPlacement(placement) {
    const pageInfo = this.pagesMap.get(placement.page)
    if (!pageInfo || !pageInfo.wrapper) return

    const color = placement.color
    const textColor = this.textColorForRef(placement.signerRef)

    const wrapper = document.createElement('div')
    wrapper.dataset.placementId = placement.id
    wrapper.dataset.placementRef = placement.signerRef
    wrapper.className = [
      'absolute flex flex-col rounded-field border-[1.5px] border-solid',
      'select-none group cursor-move touch-none',
    ].join(' ')
    // Opaque tinted fill (tint layer over white) so the document text
    // underneath doesn't bleed through and hurt readability.
    wrapper.style.cssText = [
      `background: linear-gradient(0deg, ${color}26, ${color}26), #ffffff`,
      `border-color: ${color}`,
    ].join(';')

    const header = document.createElement('div')
    header.className = 'flex items-center justify-between gap-1 px-1.5 pt-1'

    const headLeft = document.createElement('div')
    headLeft.className = 'flex items-center gap-1 min-w-0 pointer-events-none'
    headLeft.style.color = textColor
    headLeft.appendChild(this.buildInlineIcon('grip', 'opacity-50 shrink-0'))
    const name = document.createElement('span')
    name.className = 'truncate text-[11.5px] font-semibold'
    name.textContent = this.labelForRef(placement.signerRef)
    headLeft.appendChild(name)
    header.appendChild(headLeft)
    header.appendChild(this.buildRemoveButton(placement))
    wrapper.appendChild(header)

    const body = document.createElement('div')
    body.className = [
      'flex-1 m-1 mt-0.5 flex items-center justify-center gap-1',
      'rounded border border-dashed pointer-events-none text-[11px]',
    ].join(' ')
    body.style.borderColor = `${color}80`
    body.style.color = textColor
    body.appendChild(this.buildInlineIcon('pen', 'shrink-0'))
    const bodyLabel = document.createElement('span')
    bodyLabel.textContent = this.zoneLabelValue
    body.appendChild(bodyLabel)
    wrapper.appendChild(body)

    HANDLE_DIRECTIONS.forEach((direction) => {
      wrapper.appendChild(this.buildResizeHandle(placement, direction))
    })

    wrapper.addEventListener('pointerdown', (event) => {
      this.beginDrag(event, placement)
    })

    placement.element = wrapper
    pageInfo.wrapper.appendChild(wrapper)
    this.reprojectPlacement(placement)
  }

  buildInlineIcon(name, classes) {
    const span = document.createElement('span')
    span.className = classes
    span.setAttribute('aria-hidden', 'true')
    span.innerHTML = INLINE_ICONS[name] || ''
    return span
  }

  buildRemoveButton(placement) {
    const button = document.createElement('button')
    button.type = 'button'
    button.title = this.removeValue
    button.setAttribute('aria-label', this.removeValue)
    button.className = [
      'shrink-0 w-[18px] h-[18px] rounded-full',
      'bg-base-300/60 text-base-content/60',
      'flex items-center justify-center text-xs leading-none',
      'cursor-pointer hover:bg-red-50 hover:text-red-600',
      'opacity-0 group-hover:opacity-100 transition-opacity',
    ].join(' ')
    button.appendChild(this.buildInlineIcon('x', 'inline-flex'))
    button.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.removePlacement(placement)
    })
    button.addEventListener('pointerdown', (event) => {
      event.stopPropagation()
    })
    return button
  }

  buildResizeHandle(placement, direction) {
    const handle = document.createElement('div')
    handle.dataset.placementHandle = direction
    handle.className = [
      'absolute w-3 h-3 bg-white border-2 rounded-sm',
      'opacity-0 group-hover:opacity-100 transition-opacity',
      HANDLE_POSITION_CLASSES[direction],
    ].join(' ')
    handle.style.borderColor = placement.color
    handle.addEventListener('pointerdown', (event) => {
      event.stopPropagation()
      this.beginResize(event, placement, direction)
    })
    return handle
  }

  beginDrag(event, placement) {
    if (event.button !== 0) return
    const pageInfo = this.pagesMap.get(placement.page)
    if (!pageInfo) return

    event.preventDefault()
    const wrapperRect = placement.element.getBoundingClientRect()
    const offsetCssX = event.clientX - wrapperRect.left
    const offsetCssY = event.clientY - wrapperRect.top

    const onMove = (moveEvent) => {
      this.continueDrag(moveEvent, placement, pageInfo, offsetCssX, offsetCssY)
    }
    const onUp = (upEvent) => {
      try {
        placement.element.releasePointerCapture(upEvent.pointerId)
      } catch (_) { /* noop */ }
      placement.element.removeEventListener('pointermove', onMove)
      placement.element.removeEventListener('pointerup', onUp)
      placement.element.removeEventListener('pointercancel', onUp)
      placement.element.classList.remove('opacity-70')
      this.dragging = false
      this.notifyPlacementsChange()
    }

    this.dragging = true
    placement.element.setPointerCapture(event.pointerId)
    placement.element.classList.add('opacity-70')
    placement.element.addEventListener('pointermove', onMove)
    placement.element.addEventListener('pointerup', onUp)
    placement.element.addEventListener('pointercancel', onUp)
  }

  continueDrag(event, placement, pageInfo, offsetCssX, offsetCssY) {
    const pageRect = pageInfo.canvas.getBoundingClientRect()
    const dynamicScale = pageRect.width / pageInfo.pdfWidth
    const cssX = event.clientX - pageRect.left - offsetCssX
    const cssY = event.clientY - pageRect.top - offsetCssY
    const ptX = cssX / dynamicScale
    const ptYFromTop = cssY / dynamicScale

    const maxX = pageInfo.pdfWidth - placement.width
    const maxY = pageInfo.pdfHeight - placement.height
    placement.x = clamp(ptX, 0, maxX)
    placement.y = clamp(
      pageInfo.pdfHeight - ptYFromTop - placement.height,
      0,
      maxY
    )
    this.reprojectPlacement(placement)
  }

  beginResize(event, placement, direction) {
    if (event.button !== 0) return
    const pageInfo = this.pagesMap.get(placement.page)
    if (!pageInfo) return

    event.preventDefault()
    const handle = event.currentTarget
    const startState = {
      x: placement.x,
      y: placement.y,
      width: placement.width,
      height: placement.height,
      cssX: event.clientX,
      cssY: event.clientY,
    }

    const onMove = (moveEvent) => {
      this.continueResize(moveEvent, placement, direction, pageInfo, startState)
    }
    const onUp = (upEvent) => {
      try {
        handle.releasePointerCapture(upEvent.pointerId)
      } catch (_) { /* noop */ }
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onUp)
      handle.removeEventListener('pointercancel', onUp)
      this.dragging = false
      this.notifyPlacementsChange()
    }

    this.dragging = true
    handle.setPointerCapture(event.pointerId)
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onUp)
    handle.addEventListener('pointercancel', onUp)
  }

  continueResize(event, placement, direction, pageInfo, startState) {
    const pageRect = pageInfo.canvas.getBoundingClientRect()
    const dynamicScale = pageRect.width / pageInfo.pdfWidth
    const dxPt = (event.clientX - startState.cssX) / dynamicScale
    const dyPt = (event.clientY - startState.cssY) / dynamicScale

    const minW = this.minWidthValue || 80
    const maxW = this.maxWidthValue || 400
    const minH = this.minHeightValue || 30
    const maxH = this.maxHeightValue || 160

    const movesLeft = direction.includes('w')
    const movesRight = direction.includes('e')
    const movesTop = direction.includes('n')
    const movesBottom = direction.includes('s')

    let newW = startState.width
    let newH = startState.height

    if (movesLeft) newW = startState.width - dxPt
    else if (movesRight) newW = startState.width + dxPt
    if (movesTop) newH = startState.height - dyPt
    else if (movesBottom) newH = startState.height + dyPt

    newW = clamp(newW, minW, Math.min(maxW, pageInfo.pdfWidth))
    newH = clamp(newH, minH, Math.min(maxH, pageInfo.pdfHeight))

    // PDF origin is bottom-left: only the moving edge shifts the anchor.
    const newX = movesLeft
      ? startState.x + (startState.width - newW)
      : startState.x
    const newY = movesBottom
      ? startState.y - (newH - startState.height)
      : startState.y

    placement.x = clamp(newX, 0, pageInfo.pdfWidth - newW)
    placement.y = clamp(newY, 0, pageInfo.pdfHeight - newH)
    placement.width = newW
    placement.height = newH
    this.reprojectPlacement(placement)
  }

  removePlacement(placement) {
    const index = this.placements.indexOf(placement)
    if (index >= 0) this.placements.splice(index, 1)
    if (placement.element) placement.element.remove()
    this.refreshChipBadges()
    this.refreshCounter()
    this.notifyPlacementsChange()
  }

  reprojectPlacement(placement) {
    const pageInfo = this.pagesMap.get(placement.page)
    if (!pageInfo || !placement.element) return
    const projection = computeProjection(placement, pageInfo)
    applyProjection(placement.element, projection)
  }

  refreshChipBadges() {
    if (!this.hasChipsListTarget) return

    const placedRefs = new Set(this.placements.map((p) => p.signerRef))
    this.chipsListTarget
      .querySelectorAll('[data-signer-ref]')
      .forEach((chip) => {
        const placed = placedRefs.has(chip.dataset.signerRef)
        chip.dataset.signerPlaced = String(placed)

        const grip = chip.querySelector('[data-chip-icon="grip"]')
        const check = chip.querySelector('[data-chip-icon="check"]')
        if (grip) grip.classList.toggle('hidden', placed)
        if (check) check.classList.toggle('hidden', !placed)

        chip.classList.toggle('border-dashed', !placed)
        chip.classList.toggle('border-solid', placed)
        chip.classList.toggle('cursor-grab', !placed)
        chip.classList.toggle('active:cursor-grabbing', !placed)
        chip.classList.toggle('touch-none', !placed)
      })
  }

  refreshCounter() {
    const placedRefs = new Set(this.placements.map((p) => p.signerRef))
    const total = (this.signersValue || []).length
    const placedCount = (this.signersValue || []).reduce(
      (memo, signer) => memo + (placedRefs.has(signer.ref) ? 1 : 0),
      0
    )

    if (this.hasCounterTextTarget) {
      const rule = new Intl.PluralRules(this.localeValue || 'fr')
        .select(placedCount)
      const template =
        rule === 'one' ? this.counterOneValue : this.counterOtherValue
      this.counterTextTarget.textContent = interpolate(
        template,
        { count: placedCount, total: total }
      )
      const allPlaced = total > 0 && placedCount === total
      this.counterTextTarget.classList.toggle('text-success', allPlaced)
      this.counterTextTarget.classList.toggle('font-medium', allPlaced)
      this.counterTextTarget.classList.toggle(
        'text-base-content/60',
        !allPlaced
      )
    }

    if (this.hasCounterTarget) {
      this.counterTarget
        .querySelectorAll('[data-counter-dot-ref]')
        .forEach((dot) => {
          const placed = placedRefs.has(dot.dataset.counterDotRef)
          dot.classList.toggle('bg-success', placed)
          dot.classList.toggle('border-success', placed)
          dot.classList.toggle('border-base-300', !placed)
        })
    }

    this.refreshInstruction(placedCount, total)
  }

  refreshInstruction(placedCount, total) {
    if (!this.hasInstructionTarget) return

    const remaining = total - placedCount
    if (remaining <= 0) {
      this.instructionTarget.classList.add('hidden')
      return
    }

    this.instructionTarget.classList.remove('hidden')
    if (this.hasInstructionTextTarget && remaining === 1 &&
        this.instructionRemainingValue) {
      this.instructionTextTarget.textContent = this.instructionRemainingValue
    } else if (this.hasInstructionTextTarget && this.instructionValue) {
      this.instructionTextTarget.textContent = this.instructionValue
    }
  }

  labelForRef(ref) {
    const signer = (this.signersValue || []).find((s) => s.ref === ref)
    if (!signer) return ref

    const baseName = signer.name || interpolate(
      this.unnamedLabelValue,
      { index: signer.index + 1 }
    )
    if (this.modeValue === 'sequential') {
      return interpolate(this.sequentialLabelValue, {
        name: baseName,
        index: signer.index + 1,
      })
    }
    return baseName
  }

  colorForRef(ref) {
    const signer = (this.signersValue || []).find((s) => s.ref === ref)
    return signer?.color || '#028090'
  }

  textColorForRef(ref) {
    const signer = (this.signersValue || []).find((s) => s.ref === ref)
    return signer?.text_color || signer?.color || '#1F3438'
  }

  notifyPlacementsChange() {
    const placedRefs = new Set(this.placements.map((p) => p.signerRef))
    const signers = this.signersValue || []
    const complete =
      signers.length > 0 && signers.every((s) => placedRefs.has(s.ref))

    const detail = {
      documentId: this.documentIdValue,
      complete,
      placements: this.placements.map((placement) => ({
        document_id: this.documentIdValue,
        signer_ref: placement.signerRef,
        page_number: placement.page,
        x: Math.round(placement.x),
        y: Math.round(placement.y),
        width: Math.round(placement.width),
        height: Math.round(placement.height),
      })),
    }
    const event = new CustomEvent(
      'document-placer:placements-changed',
      { detail, bubbles: true }
    )
    document.dispatchEvent(event)
  }
}
